import { DsButton, DsIcon, DsTypography } from '@ice-web-app/designSystem';
import { useTranslation } from '@ice-web-app/shared-hooks';
import { Box, Divider, MobileStepper, Stack, SxProps, Theme } from '@mui/material';
import * as React from 'react';
import { TourTooltipCorner } from '../../../../assets';
import { TourLogic, TourStep } from '../types';
import { CardinalOrientation } from '../utils/positioning';
import { getCornerStyles } from '../utils/tooltipCorner';

interface TooltipProps extends TourLogic {
  tooltipRef: React.MutableRefObject<HTMLElement | undefined>;
}

export function Tooltip(props: TooltipProps) {
  const {
    next,
    prev,
    close,
    skip,
    tooltipRef,
    stepContent: {
      title,
      customFooterRenderer,
      content,
      noSkipBtn,
      finishBtnText,
      audio,
      video,
      image,
      tooltipBorderRadius,
      noFooter,
      noCloseIcon,
      noStepper,
      corner,
      contentContainerSx,
    },
    stepIndex,
    allSteps,
    tooltipPosition,
  } = {
    ...props,
  };
  const { t } = useTranslation();
  const [activePresetSteps, setActivePresetSteps] = React.useState<TourStep[]>([]);

  const [cornerStyles, setCornerStyles] = React.useState<{
    style: SxProps<Theme>;
    svgStyle: SxProps<Theme>;
  }>();

  React.useEffect(() => {
    const activeSteps = allSteps?.filter((step) => {
      return !!window.document.querySelector(step.selector);
    });
    setActivePresetSteps(activeSteps);
  }, [allSteps, stepIndex]);

  React.useEffect(() => {
    // biome-ignore lint/style/useConst: <explanation>
    let debounceTimer: any;

    const applyStyles = () => {
      const target: HTMLElement | undefined = window.document.querySelector(allSteps?.[stepIndex]?.selector) as
        | HTMLElement
        | undefined;

      const cornerStyle = getCornerStyles(tooltipPosition?.orientation, target, tooltipRef.current, corner);
      // Apply styles only when stable
      setCornerStyles(cornerStyle);
    };

    // Debounce logic to detect stabilization
    debounceTimer = setTimeout(() => {
      applyStyles();
    }, 50); // Adjust debounce duration as needed

    return () => clearTimeout(debounceTimer); // Cleanup timer on re-render
  }, [tooltipPosition, stepIndex, allSteps, tooltipRef]);

  const prevDisabled: boolean = stepIndex - 1 < 0;
  const nextDisabled: boolean = stepIndex + 1 >= allSteps.length;

  return (
    <Box
      sx={{
        borderRadius: (theme) => theme.spacing(tooltipBorderRadius || 4),
        position: 'relative',
        ...cornerStyles?.style,
        bgcolor: 'iceGray.800',
      }}
    >
      {!(corner === 'none') &&
        tooltipPosition?.orientation &&
        tooltipPosition.orientation !== CardinalOrientation.CENTER &&
        tooltipPosition.orientation !== CardinalOrientation.EAST &&
        tooltipPosition.orientation !== CardinalOrientation.NORTH &&
        tooltipPosition.orientation !== CardinalOrientation.SOUTH &&
        tooltipPosition.orientation !== CardinalOrientation.WEST && (
          <Box
            sx={{
              position: 'absolute',
              zIndex: 10000,
              '& svg>path': { fill: (theme) => `${theme.palette.iceGray[800]} !important` },
              ...cornerStyles?.svgStyle,
            }}
          >
            <TourTooltipCorner />
          </Box>
        )}
      {title && (
        <>
          <Box sx={{ px: 5, py: 4, width: '100%', zIndex: 10001, position: 'sticky' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={2} alignItems="center">
                {typeof title === 'string' ? (
                  <DsTypography variant="h5" color="customColors.tooltipText" lineClamp="1">
                    {title}
                  </DsTypography>
                ) : typeof title === 'function' ? (
                  title(props)
                ) : (
                  title
                )}
                {audio && <DsIcon icon="SOUND_ON" color="customColors.tooltipText" size="md" />}
              </Stack>

              {!noCloseIcon && (
                <DsIcon onClick={() => close()} icon="CLOSE" color="customColors.tooltipText" size="lg" />
              )}
            </Stack>
          </Box>
          <Divider variant="fullWidth" sx={{ m: 0, borderColor: 'iceGray.700' }} />
        </>
      )}

      <Stack sx={{ px: 5, pt: 4, width: '100%' }}>
        <Box sx={{ overflow: 'auto', maxHeight: '13vh', ...contentContainerSx }}>
          {typeof content === 'string' ? (
            <DsTypography variant="body1_500" color="customColors.tooltipText">
              {content}
            </DsTypography>
          ) : typeof content === 'function' ? (
            content(props)
          ) : (
            content
          )}
        </Box>

        {(image || video) && (
          <Stack sx={{ pb: { xs: 3, md: 6 }, pt: { xs: 3, md: 6 } }} direction="row" spacing={2} alignItems="center">
            {video && (
              <DsButton
                sx={{
                  borderRadius: 5,
                  bgcolor: ({ palette }) => palette.customColors.greyLightBg,
                  maxHeight: 32,
                  '&:hover': { bgcolor: ({ palette }) => `${palette.customColors.greyLightBg} !important` },
                }}
              >
                <Stack direction="row" alignItems="center">
                  <DsIcon icon="PLAY_CIRCLE_FILL" color="primary.main" size="sm" sx={{ mr: 1 }} />
                  {t('VIDEO')}
                  {/* <DsIcon icon="EYE_CLOSED" color="primary.main" size="sm" sx={{ ml: 1 }} /> */}
                </Stack>
              </DsButton>
            )}
            {audio && (
              <DsButton
                sx={{
                  borderRadius: 5,
                  bgcolor: ({ palette }) => palette.customColors.greyLightBg,
                  maxHeight: 32,
                  '&:hover': { bgcolor: ({ palette }) => `${palette.customColors.greyLightBg} !important` },
                }}
              >
                <Stack direction="row" alignItems="center">
                  <DsIcon icon="IMAGE_FILL" color="primary.main" size="sm" sx={{ mr: 1 }} />
                  {t('IMAGE')}
                  {/* <DsIcon icon="EYE_CLOSED" color="primary.main" size="sm" sx={{ ml: 1 }} /> */}
                </Stack>
              </DsButton>
            )}
          </Stack>
        )}
      </Stack>

      {!noStepper && (
        <Box
          sx={{
            px: 4,
            pb: {
              xs: 3,
              md: 5,
            },
            ...(!image &&
              !video && {
                pt: { xs: 3, md: 6 },
              }),
            width: '100%',
          }}
        >
          <MobileStepper
            variant="dots"
            sx={{
              bgcolor: 'transparent',
              p: 0,
              '& .MuiMobileStepper-dot': {
                // bgcolor: (theme) => theme.palette.customColors.greyLightBg,
                bgcolor: (theme) => theme.palette.grey[500],
                width: '6px',
                height: '6px',
              },
              '& .MuiMobileStepper-dotActive': {
                bgcolor: (theme) => theme.palette.background.paper,
                width: 40,
                borderRadius: 4,
              },
            }}
            steps={activePresetSteps?.length || 0}
            position="static"
            activeStep={activePresetSteps?.findIndex((step) => step.selector === allSteps?.[stepIndex]?.selector) || 0}
            backButton={undefined}
            nextButton={undefined}
          />
        </Box>
      )}
      {!noFooter && (
        <Stack sx={{ px: 5, pb: 4, pt: { xs: 1, md: 2 }, width: '100%', zIndex: 10001, position: 'sticky' }}>
          {customFooterRenderer ? (
            customFooterRenderer(props)
          ) : (
            <Stack direction="row" justifyContent="space-between" spacing={4} alignItems="center">
              {stepIndex === allSteps.length - 1 ? (
                <DsButton variant="contained" size="small" onClick={() => close(true)}>
                  {finishBtnText || t('DONE')}
                </DsButton>
              ) : noSkipBtn ? (
                <Box />
              ) : (
                <DsButton variant="contained" size="small" onClick={() => close(true)}>
                  {t('SKIP')}
                </DsButton>
              )}

              <Stack direction="row" alignItems="center" spacing={4}>
                <DsButton
                  sx={{
                    bgcolor: ({ palette }) => `${palette.customColors.greyLightBg} !important`,
                    borderRadius: 1,
                    '&:hover': { bgcolor: ({ palette }) => `${palette.customColors.greyLightBg} !important` },
                  }}
                  disabled={prevDisabled}
                  onClick={() => prev()}
                  justIcon
                >
                  <DsIcon icon="CHEVRON_LEFT" color="text.secondary" />
                </DsButton>
                <DsButton
                  disabled={nextDisabled}
                  onClick={() => next()}
                  justIcon
                  sx={{
                    bgcolor: ({ palette }) => `${palette.customColors.greyLightBg} !important`,
                    borderRadius: 1,
                    '&:hover': { bgcolor: ({ palette }) => `${palette.customColors.greyLightBg} !important` },
                  }}
                >
                  <DsIcon icon="CHEVRON_RIGHT" color="text.secondary" />
                </DsButton>
              </Stack>
            </Stack>
          )}
        </Stack>
      )}
    </Box>
  );
}
